import type { DealerStatus, PipelineStage, SessionUser } from '@motoveo/shared';
import { DEALER_STATUSES, PIPELINE_STAGES } from '@motoveo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { formatDate } from '../lib/format';

export function DealersListPage({ user }: { user: SessionUser }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<DealerStatus | ''>('');
  const [stage, setStage] = useState<PipelineStage | ''>('');

  const list = useQuery({
    queryKey: ['dealers', status, stage],
    queryFn: () => api.dealers.list({ status: status || undefined, pipeline_stage: stage || undefined, limit: 200 }),
  });

  return (
    <Layout user={user}>
      <div className="row-between">
        <h2>Dealers</h2>
        <div>
          <Link to="/cockpit" className="nav-link">
            Cockpit
          </Link>{' '}
          <Link to="/dealers/import" className="nav-link">
            Import CSV
          </Link>{' '}
          <button onClick={() => navigate('/dealers/new')}>+ Onboard dealer</button>
        </div>
      </div>

      <div className="toolbar">
        <label className="inline">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value as DealerStatus | '')}>
            <option value="">all</option>
            {DEALER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="inline">
          Stage
          <select value={stage} onChange={(e) => setStage(e.target.value as PipelineStage | '')}>
            <option value="">all</option>
            {PIPELINE_STAGES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        {list.data ? <span className="muted">{list.data.total} total</span> : null}
      </div>

      {list.isLoading ? (
        <p className="muted">Loading…</p>
      ) : list.isError ? (
        <p className="error">{(list.error as Error).message}</p>
      ) : list.data && list.data.items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Dealer</th>
                <th>State</th>
                <th>Status</th>
                <th>Franchise</th>
                <th>Stage</th>
                <th>Owner</th>
                <th>Contacts</th>
                <th>Reachable</th>
                <th>ISO prefs</th>
                <th>Buyer-in-hand</th>
                <th>Last touch</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((d) => (
                <tr key={d.id} className="clickable" onClick={() => navigate(`/dealers/${d.id}`)}>
                  <td>
                    <Link to={`/dealers/${d.id}`} onClick={(e) => e.stopPropagation()}>
                      {d.business_name}
                    </Link>
                  </td>
                  <td className="muted">{d.state}</td>
                  <td>
                    <span className="pill">{d.status}</span>
                  </td>
                  <td>
                    {d.franchise ? (
                      <span className="pill conf-high">franchise</span>
                    ) : (
                      <span className="muted" title="not on the blast network (franchise-only launch)">
                        —
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="pill">{d.pipeline_stage}</span>
                  </td>
                  <td className="muted">{d.relationship_owner_name ?? '—'}</td>
                  <td>{d.contact_count}</td>
                  <td>
                    {d.reachable_contact_count > 0 ? (
                      <span className="ok-dot" title={`${d.reachable_contact_count} reachable`}>
                        ● {d.reachable_contact_count}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {d.active_iso_pref_count}
                    <span className="muted"> / {d.iso_pref_count}</span>
                  </td>
                  <td>{d.has_named_buyer ? <span className="pill conf-high">buyer</span> : <span className="muted">—</span>}</td>
                  <td className="muted">{formatDate(d.last_interaction_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">No dealers yet. Start with “Onboard dealer”.</p>
      )}
    </Layout>
  );
}
